<template>
  <div class="supplier-type-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('supplierTypeManage.list.title')" :subtitle="$t('supplierTypeManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.typeCd" clearable
            :placeholder="$t('supplierTypeManage.search.typeCd')" @keyup.enter.native="handleSearch" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.typeName" clearable
            :placeholder="$t('supplierTypeManage.search.typeName')" @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('supplierTypeManage.list.title') }}</span>
        </template>
        <template #right>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleAdd">
            {{ $t('common.add') }}
          </el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('supplierTypeManage.page.empty')" @retry="getList">
        <el-table-column prop="typeCd" :label="$t('supplierTypeManage.table.typeCd')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.typeCd || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="typeName" :label="$t('supplierTypeManage.table.typeName')" align="left" min-width="160" show-overflow-tooltip />
        <el-table-column prop="createTime" :label="$t('supplierTypeManage.table.createTime')" align="center" width="170">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('supplierTypeManage.table.remark')" align="center" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.remark || '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="160" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleEdit(scope.row)">{{ $t('common.edit') }}</el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="handleDelete(scope.row)">{{ $t('common.delete') }}</el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <!-- 子组件 -->
    <add-supplier-type ref="addSupplierType" @success="handleSuccess" />
    <edit-supplier-type ref="editSupplierType" @success="handleSuccess" />
    <delete-supplier-type ref="deleteSupplierType" @success="handleSuccess" />
  </div>
</template>

<script>
import { listSupplierType } from '@/api/scm/supplierTypeManageApi'
import AddSupplierType from '@/components/scm/AddSupplierType'
import EditSupplierType from '@/components/scm/EditSupplierType'
import DeleteSupplierType from '@/components/scm/DeleteSupplierType'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'SupplierTypeManageList',
  components: {
    AddSupplierType,
    EditSupplierType,
    DeleteSupplierType,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        typeCd: '',
        typeName: ''
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.page.current,
          row: this.page.size,
          ...this.searchForm
        }
        const { data, records, total } = await listSupplierType(params)
        this.tableData = Array.isArray(data) ? data : []
        this.page.total = total || records || 0
      } catch (error) {
        console.error('查询供应商类型失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('supplierTypeManage.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm = {
        typeCd: '',
        typeName: ''
      }
      this.page.current = 1
      this.getList()
    },
    handleAdd() {
      this.$refs.addSupplierType.open()
    },
    handleEdit(row) {
      this.$refs.editSupplierType.open(row)
    },
    handleDelete(row) {
      this.$refs.deleteSupplierType.open(row)
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.supplier-type-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
